import { cn } from '@/lib/utils';
import { useId } from 'react';
import { waveAmplitude } from './appearance-controls.util';
import { useValueControl } from './use-value-control.hook';

const WIDTH = 200;
const MIDDLE = 12;
const WAVELENGTH = 14;
/** The track's inset each side, in pixels, so the thumb stays inside. */
const INSET = 8;

/** A sine wave from `from` to `to` across the track, as an SVG path. */
function wave(from: number, to: number, amplitude: number): string {
  if (to <= from) return '';
  const xs: number[] = [];
  for (let x = from; x < to; x += 1) xs.push(x);
  xs.push(to);
  return xs
    .map((x, index) => {
      const y = MIDDLE + amplitude * Math.sin((x / WAVELENGTH) * 2 * Math.PI);
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

/**
 * Intensity, 0 to 100, as a wavy slider: the wave grows with the value, bold
 * up to the thumb and faint after it. Disabled, it takes no input and leaves
 * the tab order.
 */
export function IntensitySlider({
  value,
  disabled = false,
  onChange,
  onCommit,
  onCancel,
}: {
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  onCommit: () => void;
  onCancel: () => void;
}) {
  const labelId = useId();
  const control = useValueControl({
    value,
    onChange,
    onCommit,
    onCancel,
    valueAt: (event, rect) => {
      const width = rect.width - INSET * 2;
      const share = width > 0 ? (event.clientX - rect.left - INSET) / width : 0;
      return Math.min(1, Math.max(0, share)) * 100;
    },
  });
  const amplitude = waveAmplitude(value);
  const split = (value / 100) * WIDTH;

  return (
    <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
      <span
        id={labelId}
        className='text-[12px] font-medium text-muted-foreground'
      >
        Intensity
      </span>
      <div
        role='slider'
        tabIndex={disabled ? -1 : 0}
        aria-labelledby={labelId}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value}
        aria-valuetext={`${value}%`}
        aria-disabled={disabled || undefined}
        data-amplitude={amplitude}
        {...(disabled ? {} : control)}
        className={cn(
          'relative h-8 touch-none rounded-lg select-none',
          disabled ? 'opacity-40' : 'cursor-pointer',
          'ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden'
        )}
      >
        <svg
          aria-hidden
          viewBox={`0 0 ${WIDTH} 24`}
          preserveAspectRatio='none'
          className='absolute inset-x-2 top-1 h-6 w-[calc(100%-16px)] overflow-visible'
        >
          <path
            d={wave(0, split, amplitude)}
            fill='none'
            strokeWidth={2.5}
            strokeLinecap='round'
            vectorEffect='non-scaling-stroke'
            className='stroke-foreground'
          />
          <path
            d={wave(split, WIDTH, amplitude)}
            fill='none'
            strokeWidth={2.5}
            strokeLinecap='round'
            vectorEffect='non-scaling-stroke'
            className='stroke-muted-foreground/35'
          />
        </svg>
        <span
          aria-hidden
          className='absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-foreground bg-background shadow-xs'
          style={{
            left: `calc(${INSET}px + (100% - ${INSET * 2}px) * ${value / 100})`,
          }}
        />
      </div>
    </div>
  );
}
